import { useEffect, useState, useRef } from "react";
import { Send } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Restaurants from "../../components/AIChat/Restaurants";
import Menu from "../../components/AIChat/Menu";
import BooKTable from "../../components/AIChat/BookTable";
import TableBookings from "../../components/AIChat/TableBookings";
import { BiSolidBot } from "react-icons/bi";
import { useAuth } from "../../context/AuthContext";
import {
  askSupportAsLoggedInUser,
  askSupportAsGuest,
  getMessages,
} from "../../api/common/AIChat";
import { getUserById } from "../../api/common";
import { useTranslation } from "../../hooks/useTranslation";

export default function AIChat({ userData, userLanguage = "en" }) {
  const messagesEndRef = useRef(null);

  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  };
  const { t } = useTranslation();
  const [filterType,setFilterType]  = useState({})
  const [messages, setMessages] = useState([
    { from: "bot", text: t("aiChat.welcomeMessage"), timestamp: new Date().toISOString() },
  ]);
  const [input, setInput] = useState("");
  const [restaurantId, setRestaurantId] = useState(null);
  const [reservationId, setReservationId] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["ai-chats"],
    queryFn: getMessages,
  });

  // get user
  const { data: authUserData } = useQuery({
    queryKey: ["user"],
    queryFn: getUserById,
  });


  useEffect(() => {
    if (data?.data?.chats?.length) {
      const formatted = data.data.chats.flatMap((chat) => [
        { from: "user", text: chat.userQuery, timestamp: chat.createdAt },
        { from: "bot", text: chat.aiResponse, timestamp: chat.updatedAt || chat.createdAt },
      ]);
      setMessages(formatted);
    }
  }, [data]);

  // Auto-scroll when messages change
  useEffect(() => {
    // Use a small timeout to ensure the DOM has updated
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [messages]);

  const { mutate: sendMessage, isPending: isTyping } = useMutation({
    mutationFn: async (question) => {
      if (userData?._id) {
        // logged in user
        return await askSupportAsLoggedInUser({
          question,
          language: userLanguage,
        });
      } else {
        // guest user
        return await askSupportAsLoggedInUser({
          question,
          language: userLanguage,
        });
      }
    },
    onSuccess: (res) => {
      const { reply, tool, restaurantId, reservationId, url ,filterType } = res.data;
setFilterType(filterType)
      if (reply) {
        setMessages((prev) => [...prev, { from: "bot", text: reply, timestamp: new Date().toISOString() }]);
      }

      setRestaurantId(restaurantId);

      if (reservationId) {
        setReservationId(reservationId);
      }

      if (url) {
        setPaymentUrl(url);
      }

      // Tool → Render specific component inline
      if (tool === "showRestaurants") {
        const currentTime = new Date().toISOString();
        setMessages((prev) => [
          ...prev,
          // { from: "bot", text: "Here are some restaurants:" },
          { from: "component", component: "restaurants", timestamp: currentTime },
        ]);
      }
      if (tool === "viewMenu") {
        const currentTime = new Date().toISOString();
        setMessages((prev) => [
          ...prev,
          // { from: "bot", text: "Here's the menu:" },
          { from: "component", component: "menu", timestamp: currentTime },
        ]);
      }
      if (tool === "bookTable") {
        const currentTime = new Date().toISOString();
        setMessages((prev) => [
          ...prev,
          { from: "bot", text: "Let's book a table!", timestamp: currentTime },
          { from: "component", component: "booking", timestamp: currentTime },
        ]);
      }
      if (tool === "orders") {
        const currentTime = new Date().toISOString();
        setMessages((prev) => [
          ...prev,
          // { from: "bot", text: "Here are your orders:" },
          { from: "component", component: "orders", timestamp: currentTime },
        ]);
      }
      if (tool === "getTableBookings") {
        const currentTime = new Date().toISOString();
        setMessages((prev) => [
          ...prev,
          // { from: "bot", text: "Here are your table bookings:" },
          { from: "component", component: "tableBookings", timestamp: currentTime },
        ]);
      }
      if (tool === "addItems") {
        const currentTime = new Date().toISOString();
        setMessages((prev) => [
          ...prev,
          // { from: "bot", text: "Here are your table bookings:" },
          { from: "component", component: "menu", timestamp: currentTime },
        ]);
      }
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: "⚠️ Sorry, something went wrong.", timestamp: new Date().toISOString() },
      ]);
    },
  });

  const handleSend = () => {
    if (!input.trim()) return;

    // Add user message with current timestamp
    const currentTime = new Date().toISOString();
    setMessages((prev) => [...prev, { from: "user", text: input, timestamp: currentTime }]);

    // Trigger backend AI
    sendMessage(input);

    // Clear input
    setInput("");
  };

  return (
    <div className="mx-auto p-4 flex flex-col h-[700px] rounded-xl bg-white shadow-lg border border-gray-100">
      {/* Chat Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-100 bg-white sticky top-0 z-10">
        <div className="w-12 h-12 rounded-full bg-[#FFF3EF] flex items-center justify-center">
          <BiSolidBot size={28} className="text-black" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-800">{t("aiChat").title}</h2>
          <p className="text-sm text-gray-500">{t("aiChat").subtitle}</p>
        </div>
      </div>

      {/* Messages */}
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto px-4" style={{ scrollbarWidth: 'thin' }}>
        <div className="py-6 pb-24 space-y-6">
          {messages.map((msg, i) => {
            if (msg.component === "restaurants") return <Restaurants filterType={filterType} key={i} />;
            if (msg.component === "menu")
              return (
                <Menu
                  key={i}
                  restaurantId={restaurantId}
                  reservationId={reservationId}
                />
              );
            if (msg.component === "booking")
              return <BooKTable key={i} restaurantId={restaurantId} />;
            if (msg.component === "tableBookings")
              return <TableBookings key={i} restaurantId={restaurantId} />;

            if (paymentUrl) {
              return <Pay key={i} url={paymentUrl} />;
            }

            return (
              <div
                key={i}
                className={`flex items-start gap-2 ${msg.from === "bot" ? "justify-start" : "justify-end"}`}
              >
                {msg.from === "bot" && (
                  <div className="w-12 h-12 rounded-full bg-[#FFF3EF] flex items-center justify-center shadow-sm">
                    <BiSolidBot size={28} className="text-black" />
                  </div>
                )}
                <div className="flex flex-col flex-1">
                  <div
                    className={`px-5 py-3 rounded-2xl ${msg.from === "bot"
                      ? "bg-gray-100 text-black rounded-bl-none mr-12"
                      : "bg-[#4CAF50] text-white rounded-br-none ml-12 self-end"
                      }`}
                  >
                    <p className="text-[15px] leading-relaxed">{msg.text}</p>
                  </div>
                  <span className={`text-xs text-gray-400 mt-1 ${msg.from === "bot" ? "ml-2" : "mr-2 self-end"}`}>
                    {msg.timestamp 
                      ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }
                  </span>
                </div>
                {msg.from === "user" && (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden shadow-sm">
                    {authUserData?.data?.profilePicture ? (
                      <img
                        src={authUserData?.data?.profilePicture}
                        alt="User"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-blue-500 flex items-center justify-center text-white text-base font-medium">
                        {authUserData?.data?.name ? authUserData.data.name[0].toUpperCase() : "U"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {isTyping && (
            <div className="flex justify-start">
              <div className="px-4 py-2 rounded-lg bg-gray-200 text-black rounded-bl-none">
                ⌛ {t("aiChat").sending}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />

      {/* Input box */}
      <div className="mt-4 flex items-center gap-3 border-t border-gray-100 p-4 bg-white sticky bottom-0">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("aiChat").typingPlaceholder}
          className="flex-1 px-5 py-3 bg-gray-50 rounded-full border border-gray-200 focus:outline-none focus:border-[#4CAF50] focus:ring-1 focus:ring-[#4CAF50] placeholder-gray-400"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !isTyping && input.trim()) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <button
          onClick={handleSend}
          disabled={isTyping || !input.trim()}
          className={`p-3 rounded-full w-12 h-12 flex items-center justify-center transition-all transform hover:scale-105 ${isTyping || !input.trim()
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-[#4CAF50] text-white hover:bg-[#45a049] hover:shadow-md"
            }`}
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

// components/AIChat/Pay.js
function Pay({ url }) {
  return (
    <div className="w-full h-[400px] bg-gray-100 rounded-lg overflow-hidden">
      <iframe
        src={url}
        title="Payment Gateway"
        width="600"
        height="400"
        frameBorder="0"
      />
    </div>
  );
}